"use client";

import { Link, MapPin, Navigation, Search, X } from "lucide-react";
import { useRef, useState } from "react";

import { DEFAULT_LAT, DEFAULT_LNG } from "./_inspection/inspectionConstants";
import { useGoogleMaps } from "./_inspection/useGoogleMaps";

type LocationInputMode = "map" | "link";

interface LocationPickerProps {
  onLocationChange: (address: string, lat: number, lng: number) => void;
  t: (key: string) => string;
}

const TabBtn = ({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "inter relative z-10 flex flex-1 items-center justify-center gap-2 rounded-lg px-1 py-2.5 text-sm font-medium transition-colors duration-200",
      active ? "bg-gray-800 text-white" : "text-secondary-foreground hover:text-primary-foreground",
    ].join(" ")}
  >
    <span className={["transition-transform duration-300", active ? "scale-110" : "scale-100"].join(" ")}>{icon}</span>
    <span className="hidden leading-tight sm:inline">{label}</span>
  </button>
);

const LocationPicker = ({ onLocationChange, t }: LocationPickerProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isClearingRef = useRef(false);

  const [inputMode, setInputMode] = useState<LocationInputMode>("map");
  const [displayAddress, setDisplayAddress] = useState("");
  const [gettingLocation, setGettingLocation] = useState(false);

  const [mapsLink, setMapsLink] = useState("");
  const [mapsLinkError, setMapsLinkError] = useState("");

  const handleAddressResolved = (address: string, lat: number, lng: number) => {
    if (isClearingRef.current) return;
    setDisplayAddress(address);
    if (searchInputRef.current) searchInputRef.current.value = address;
    onLocationChange(address, lat, lng);
  };

  const handlePlaceSelected = (address: string, lat: number, lng: number) => {
    if (isClearingRef.current) return;
    setDisplayAddress(address);
    if (searchInputRef.current) searchInputRef.current.value = address;
    onLocationChange(address, lat, lng);
  };

  const { isLoaded, loadError, movePinTo } = useGoogleMaps(mapContainerRef, searchInputRef, {
    onAddressResolved: handleAddressResolved,
    onPlaceSelected: handlePlaceSelected,
  });

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        movePinTo(coords.latitude, coords.longitude, 17);
        setGettingLocation(false);
      },
      () => setGettingLocation(false),
    );
  };

  const handleClearAddress = () => {
    isClearingRef.current = true;
    setDisplayAddress("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
      searchInputRef.current.focus();
    }
    onLocationChange("", DEFAULT_LAT, DEFAULT_LNG);
    movePinTo(DEFAULT_LAT, DEFAULT_LNG, 12, false);
    setTimeout(() => {
      isClearingRef.current = false;
    }, 500);
  };

  const validateMapsLink = (val: string) => {
    if (!val) return "";
    const isValid =
      val.includes("maps.google") ||
      val.includes("goo.gl/maps") ||
      val.includes("maps.app.goo.gl") ||
      val.includes("google.com/maps");
    return isValid ? "" : t("mapsLinkError");
  };

  const handleMapsLinkChange = (val: string) => {
    setMapsLink(val);
    const error = validateMapsLink(val);
    setMapsLinkError(error);
    if (!error && val) {
      onLocationChange(val, DEFAULT_LAT, DEFAULT_LNG);
    } else if (!val) {
      onLocationChange("", DEFAULT_LAT, DEFAULT_LNG);
    }
  };

  const tabSliderClass = inputMode === "map" ? "left-1 w-[calc(50%-5px)]" : "left-[calc(50%+1px)] w-[calc(50%-5px)]";

  if (loadError && inputMode === "map") {
    return (
      <div className="flex h-40 items-center justify-center rounded-2xl border border-red-200 bg-red-50">
        <p className="inter text-sm text-red-500">{t("mapLoadError")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ── 2-tab toggle ── */}
      <div className="relative flex rounded-xl border border-gray-200 bg-secondary p-1">
        <div
          className={[
            "absolute top-1 h-[calc(100%-8px)] rounded-lg bg-white shadow-sm transition-all duration-300 ease-in-out",
            tabSliderClass,
          ].join(" ")}
        />
        <TabBtn
          active={inputMode === "map"}
          onClick={() => setInputMode("map")}
          icon={<MapPin size={14} />}
          label={t("locationModeMap")}
        />
        <TabBtn
          active={inputMode === "link"}
          onClick={() => setInputMode("link")}
          icon={<Link size={14} />}
          label={t("locationModeLink")}
        />
      </div>

      {/* ── Map mode ── */}
      <div
        className={[
          "space-y-3 transition-all duration-300 ease-in-out",
          inputMode === "map"
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none absolute -z-10 h-0 translate-y-2 overflow-hidden opacity-0",
        ].join(" ")}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-4">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              defaultValue=""
              onChange={(e) => {
                if (!isClearingRef.current) setDisplayAddress(e.target.value);
              }}
              placeholder={t("searchAddressPlaceholder")}
              className="inter w-full rounded-2xl border border-gray-200 bg-white py-3 pe-10 ps-10 text-sm text-primary-foreground outline-none placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
            />
            {displayAddress && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleClearAddress();
                }}
                className="absolute inset-y-0 end-0 flex items-center pe-4 text-gray-400 hover:text-gray-600"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleUseMyLocation}
            disabled={gettingLocation}
            className="inter flex w-full items-center justify-center gap-2 rounded-2xl border border-primary bg-primary px-4 py-3 text-sm font-medium text-white transition-all hover:bg-primary-light hover:text-primary disabled:opacity-50 sm:w-auto sm:whitespace-nowrap"
          >
            <Navigation size={15} className={gettingLocation ? "animate-pulse text-primary" : ""} />
            {gettingLocation ? t("gettingLocation") : t("useMyLocation")}
          </button>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-gray-200">
          {!isLoaded && (
            <div className="flex h-72 items-center justify-center bg-gray-50">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}
          <div ref={mapContainerRef} className={isLoaded ? "h-72 w-full" : "hidden"} />
          {isLoaded && (
            <div className="pointer-events-none absolute bottom-2 left-2 xl:bottom-3 xl:left-1/2 xl:-translate-x-1/2">
              <span className="inter rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-secondary-foreground backdrop-blur-sm">
                {t("mapDragHint")}
              </span>
            </div>
          )}
        </div>

        {displayAddress && (
          <div className="flex items-start gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <MapPin size={15} className="mt-0.5 shrink-0 text-primary" />
            <p className="inter text-sm text-primary-foreground">{displayAddress}</p>
          </div>
        )}
      </div>

      {/* ── Link mode ── */}
      <div
        className={[
          "space-y-2 transition-all duration-300 ease-in-out",
          inputMode === "link"
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none absolute -z-10 h-0 translate-y-2 overflow-hidden opacity-0",
        ].join(" ")}
      >
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-4">
            <Link size={15} className="text-gray-400" />
          </div>
          <input
            type="url"
            value={mapsLink}
            onChange={(e) => handleMapsLinkChange(e.target.value)}
            placeholder={t("mapsLinkPlaceholder")}
            className={[
              "inter w-full rounded-2xl border bg-white py-3 pe-10 ps-10 text-sm text-primary-foreground outline-none placeholder:text-gray-400 focus:ring-1",
              mapsLinkError
                ? "border-red-300 focus:border-red-400 focus:ring-red-200"
                : "border-gray-200 focus:border-primary focus:ring-primary",
            ].join(" ")}
          />
          {mapsLink && (
            <button
              type="button"
              onClick={() => {
                setMapsLink("");
                setMapsLinkError("");
                onLocationChange("", DEFAULT_LAT, DEFAULT_LNG);
              }}
              className="absolute inset-y-0 end-0 flex items-center pe-4 text-gray-400 hover:text-gray-600"
            >
              <X size={15} />
            </button>
          )}
        </div>

        {mapsLinkError && <p className="inter text-xs text-red-500">{mapsLinkError}</p>}

        {mapsLink && !mapsLinkError && (
          <div className="flex items-start gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <MapPin size={15} className="mt-0.5 shrink-0 text-primary" />
            <p className="inter text-sm text-primary-foreground">{mapsLink}</p>
          </div>
        )}

        <p className="inter px-1 text-xs text-secondary-foreground">{t("mapsLinkHint")}</p>
      </div>
    </div>
  );
};

export default LocationPicker;
